import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Globe, Lock, Shield, ExternalLink } from 'lucide-react';

const SitemapPage = () => {
  const publicRoutes = [
    { path: '/', name: 'Home', description: 'Landing page with features, demo, pricing, and testimonials' },
    { path: '/about', name: 'About Us', description: 'Learn about iStudyPRO and our mission' },
    { path: '/educators', name: 'For Educators', description: 'How educators can leverage iStudyPRO' },
    { path: '/developers', name: 'Developers', description: 'API documentation, tech stack, and developer resources' },
    { path: '/legal', name: 'Legal', description: 'Privacy Policy, Terms of Service, Cookie Policy, FERPA, GDPR, AUP, DPA' },
    { path: '/login', name: 'Sign In', description: 'Log in to your iStudyPRO account' },
    { path: '/verify-email', name: 'Verify Email', description: 'Email verification for new accounts' },
    { path: '/payment/callback', name: 'Payment Callback', description: 'Payment confirmation and processing' },
    { path: '/sitemap', name: 'Sitemap', description: 'This page — full site map of iStudyPRO' },
  ];

  const appRoutes = [
    { path: '/app', name: 'Dashboard', description: 'Your study overview, due reviews, and activity feed' },
    { path: '/subjects', name: 'Subjects', description: 'Manage your subjects and courses' },
    { path: '/lectures', name: 'Lectures', description: 'Record, transcribe, and review lectures with AI' },
    { path: '/study-buddy', name: 'AI Study Buddy', description: 'Chat with your AI-powered study assistant' },
    { path: '/learning-bytes', name: 'Learning Bytes', description: 'Quick study content and micro-lessons' },
    { path: '/schedule', name: 'Study Schedule', description: 'Plan and organise your study sessions' },
    { path: '/study-plan', name: 'Study Plan', description: 'AI-generated personalised study plans' },
    { path: '/review', name: 'Spaced Repetition', description: 'Flashcard reviews with SM-2 scheduling' },
    { path: '/exam-mode', name: 'Exam Mode', description: 'Timed practice exams and past questions' },
    { path: '/exam-analytics', name: 'Exam Analytics', description: 'Performance breakdown across exams' },
    { path: '/progress', name: 'Progress', description: 'Track your overall learning progress' },
    { path: '/recommendations', name: 'Recommendations', description: 'AI-curated study material suggestions' },
    { path: '/study-groups', name: 'Study Groups', description: 'Collaborate with classmates in real time' },
    { path: '/achievements', name: 'Achievements', description: 'Badges, streaks, and gamification rewards' },
    { path: '/referrals', name: 'Referrals', description: 'Invite friends and earn rewards' },
    { path: '/document-uploads', name: 'Document Uploads', description: 'Upload PDFs, slides, and study materials' },
    { path: '/campus', name: 'Campus', description: 'B2B campus plan management and analytics' },
    { path: '/analytics', name: 'Analytics Dashboard', description: 'Detailed usage and performance analytics' },
    { path: '/pricing', name: 'Pricing', description: 'Compare plans and manage your subscription' },
    { path: '/addons', name: 'Add-ons', description: 'Premium add-ons and extra features' },
    { path: '/support', name: 'Support', description: 'Helpdesk, tickets, and FAQ knowledge base' },
    { path: '/settings', name: 'Settings', description: 'Account, preferences, and notification settings' },
    { path: '/app/profile', name: 'Profile', description: 'Your profile and account information' },
  ];

  const adminRoutes = [
    { path: '/app/admin', name: 'Admin Dashboard', description: 'Ambassadors, revenue, support tickets, audit log, storage, and add-ons revenue' },
  ];

  const landingSections = [
    { anchor: '#features', name: 'Features', description: 'Live Transcription, Confusion Button, Cornell Notes, Quiz Generator' },
    { anchor: '#live-demo', name: 'Live Demo', description: 'Interactive demo of the transcription engine' },
    { anchor: '#pricing', name: 'Pricing', description: 'Free, Student, and Pro individual plans' },
    { anchor: '#campus-pricing', name: 'Campus Plans', description: 'B2B pricing for departments and institutions' },
    { anchor: '#testimonials', name: 'Testimonials', description: 'Student success stories from across Ghana and Africa' },
    { anchor: '#footer', name: 'Contact & Newsletter', description: 'Subscribe, contact info, and social links' },
  ];

  const RouteGroup = ({ title, icon: Icon, routes, isLink, iconColor }) => (
    <div className="mb-10">
      <div className="flex items-center gap-3 mb-5">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${iconColor}`}>
          <Icon className="w-4.5 h-4.5 text-white" />
        </div>
        <h2 className="text-lg font-bold text-[var(--isp-heading)]">{title}</h2>
      </div>
      <div className="grid gap-2">
        {routes.map((route) => (
          <Link
            key={route.path || route.anchor}
            to={isLink ? (route.path || `/${route.anchor}`) : (route.path || `/${route.anchor}`)}
            className="group flex items-center justify-between gap-4 px-4 py-3 rounded-xl border border-[var(--isp-glass-border)] bg-[var(--isp-page)] hover:border-indigo-200 hover:bg-indigo-50/40 transition-all"
            data-testid={`sitemap-link-${(route.path || route.anchor || '').replace(/[/#]/g, '-').replace(/^-/, '')}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-[var(--isp-heading)] text-sm group-hover:text-indigo-700 transition-colors">{route.name}</span>
                <code className="text-xs text-[var(--isp-muted)] bg-[var(--isp-glass-inner)] px-1.5 py-0.5 rounded hidden sm:inline">{route.path || route.anchor}</code>
              </div>
              <p className="text-xs text-[var(--isp-body)] mt-0.5 truncate">{route.description}</p>
            </div>
            <ExternalLink className="w-4 h-4 text-[var(--isp-muted)] group-hover:text-indigo-500 flex-shrink-0 transition-colors" />
          </Link>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--isp-page)]" data-testid="sitemap-page">
      {/* Header */}
      <div className="bg-[var(--isp-page)] border-b border-[var(--isp-glass-border)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[var(--isp-body)] hover:text-indigo-600 transition-colors mb-4" data-testid="sitemap-back-link">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-[var(--isp-heading)]" data-testid="sitemap-heading">Sitemap</h1>
          <p className="text-sm text-[var(--isp-body)] mt-2">
            Complete map of all pages and sections on <span className="font-medium text-[var(--isp-heading)]">iStudyPRO</span> — a subsidiary of I-Assist Pro Limited.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <RouteGroup title="Public Pages" icon={Globe} routes={publicRoutes} isLink iconColor="bg-emerald-500" />
        <RouteGroup title="Homepage Sections" icon={Globe} routes={landingSections} isLink={false} iconColor="bg-indigo-500" />
        <RouteGroup title="App Pages (Logged In)" icon={Lock} routes={appRoutes} isLink iconColor="bg-purple-500" />
        <RouteGroup title="Admin Pages" icon={Shield} routes={adminRoutes} isLink iconColor="bg-amber-500" />

        {/* Footer info */}
        <div className="mt-10 pt-6 border-t border-[var(--isp-glass-border)] text-center">
          <p className="text-xs text-[var(--isp-muted)]">
            © 2026 iStudyPRO. A subsidiary of I-Assist Pro Limited. | <a href="https://www.istudypro.net" className="hover:text-slate-600 transition-colors">www.istudypro.net</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SitemapPage;
